import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  
  const quickLinks = [
    { name: t('usaVisa'), path: '/services/usa-urgent-visa' },
    { name: t('ukVisa'), path: '/services/urgent-uk-visa' },
    { name: t('schengenVisa'), path: '/services/urgent-schengen-visa' },
    { name: t('canadaVisa'), path: '/services/canada-visa' },
    { name: t('australiaVisa'), path: '/services/australia-visa' },
  ];

  const otherVisas = [
    { name: t('japanVisa'), path: '/visas/japan' },
    { name: t('chinaVisaFooter'), path: '/visas/china' },
    { name: t('cyprusVisa'), path: '/visas/cyprus' },
    { name: t('russiaVisa'), path: '/visas/russia' },
    { name: t('singaporeVisa'), path: '/visas/singapore' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          
          {/* Company Info */}
          <div>
            <Link to="/" className="inline-flex items-center space-x-3 mb-4">
              <img 
                src="/logo-transparent.png" 
                alt="The Visagram" 
                className="h-16 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">The Visagram</span>
                <span className="text-xs text-gray-400 -mt-1">Visa Services Agency</span>
              </div>
            </Link>
            <p className="text-gray-400 text-sm mb-4">
              {t('footerCompanyDescription')}
            </p>
          </div>

          {/* Popular Visas */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-logo-cyan">{t('popularVisas')}</h3>
            <ul className="space-y-2 text-sm">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-logo-cyan transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Other Visas */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-logo-cyan">{t('footerOtherVisas')}</h3>
            <ul className="space-y-2 text-sm">
              {otherVisas.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-logo-cyan transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-logo-cyan">{t('footerContactUs')}</h3>
            <div className="space-y-3 text-sm">
              <a 
                href="tel:+971585862103" 
                className="flex items-center text-gray-400 hover:text-logo-cyan transition-colors"
              >
                <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>+971 58 586 2103</span>
              </a>
              <a 
                href="mailto:info@thevisagram.ae" 
                className="flex items-center text-gray-400 hover:text-logo-cyan transition-colors"
              >
                <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>info@thevisagram.ae</span>
              </a>
              <div className="flex items-start text-gray-400">
                <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white mb-1">The Visagram - B Hive</p>
                  <p>Level 3 Burjuman Mall</p>
                  <p>Khalid Bin Al Waleed Rd - Dubai UAE</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6 flex space-x-3">
              <a
                href="https://www.facebook.com/profile.php?id=61589889036356"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors p-1.5"
              >
                <img src="/facebook-icon.png" alt="Facebook" className="w-full h-full object-contain" />
              </a>
              <a
                href="https://wa.me/971585862103"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors p-2"
              >
                <img src="/whatsapp-icon.png" alt="WhatsApp" className="w-full h-full object-contain" />
              </a>
              <a
                href="https://www.instagram.com/thevisagram.ae"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors p-1.5"
              >
                <img src="/instagram-icon.png" alt="Instagram" className="w-full h-full object-contain" />
              </a>
              <a
                href="https://www.linkedin.com/company/thevisagram"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors p-1.5"
              >
                <img src="/linkedin-icon.png" alt="LinkedIn" className="w-full h-full object-contain" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-6 text-center text-sm text-gray-400">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p>{t('footerCopyright')}</p>
            <div className="flex items-center space-x-4">
              <Link to="/privacy-policy" className="hover:text-logo-cyan transition-colors">
                {t('privacyPolicy')}
              </Link>
              <span>•</span>
              <p>
                {t('madeWithLove')}{' '}
                <a
                  href="https://votivadigital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-300 transition-colors font-semibold"
                >
                  votivadigital.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
